import React, { useMemo, useState } from 'react';
import { Incident, Communication } from './EmailGeneration';
import { generateLLMKnowledgeArticle, saveKnowledgeArticle } from '../../api/fetchIncidents';

interface KnowledgeArticleProps {
  incident: Incident;
  communications: Communication[];
  closingNotes: string;
  onClose: () => void;
  onSaved?: (url?: string) => void; // NEW callback
}

export default function KnowledgeArticle({
  incident,
  communications,
  closingNotes,
  onClose,
  onSaved
}: KnowledgeArticleProps) {
  // Combined Resolution + Root Cause
  const [resolutionRootCause, setResolutionRootCause] = useState(
    closingNotes ? `${closingNotes}\n\nRoot Cause: ` : 'Root Cause: '
  );
  const [title, setTitle] = useState(`Incident ${incident.id} – ${incident.title}`);
  const [problem, setProblem] = useState(incident.description || '');
  const [impact, setImpact] = useState(incident.businessImpact || '');
  const [nextSteps, setNextSteps] = useState('');
  const [generating, setGenerating] = useState(false);
  const [combinedPreview, setCombinedPreview] = useState<string | null>(null);
  const [showPane, setShowPane] = useState<'original' | 'preview'>('original');
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [docUrl, setDocUrl] = useState<string | null>(null); // NEW

  // Aggregate all non-circumvented updates (chronological)
  const allUpdatesText = useMemo(() => {
    const ordered = [...communications]
      .filter(c => !c.iscircumvented)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    if (!ordered.length) return '';
    return ordered
      .map((c, idx) => {
        const label = idx === 0 ? 'Initial Update' : `Update ${idx}`;
        return `${label}: ${c.content.trim()}`;
      })
      .join('\n\n');
  }, [communications]);

  const disableSave = !title.trim() || !resolutionRootCause.trim();

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    console.log('[KA] Saving article...');
    try {
      const payload = {
        incident_id: incident.id,
        title,
        problem,
        impact,
        resolution_root_cause: resolutionRootCause,
        next_steps: nextSteps,
        updates: allUpdatesText,
        combined_markdown: combinedPreview
      };
      console.log('[KA] Payload:', payload);
      const resp = await saveKnowledgeArticle(payload);
      console.log('[KA] Raw response:', resp);
      if (resp.success) {
        setSavedId(resp.saved_id || resp.incident_id || null);
        const link = resp.docx_url || resp.ka_url || null;
        setDocUrl(link);
        if (onSaved) onSaved(link || undefined); // notify parent
        if (!link) {
          console.warn('[KA] No document URL (docx_url / ka_url) in response.');
        }
        // keep dialog open
      } else {
        console.warn('[KA] Save response not successful');
      }
    } catch (e) {
      console.warn('[KA] Save failed', e);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateLLM = async () => {
    if (generating) return;
    setGenerating(true);
    setCombinedPreview(null);
    try {
      const resp = await generateLLMKnowledgeArticle({
        incident_id: incident.id,
        title,
        problem,
        impact,
        resolution_root_cause: resolutionRootCause,
        next_steps: nextSteps,
        updates: allUpdatesText
      });
      if (resp.success) {
        // Optionally update fields with refined content
        setResolutionRootCause(resp.resolution_root_cause);
        setNextSteps(resp.next_steps);
        setCombinedPreview(resp.combined_markdown || null);
        setShowPane('preview'); // auto-switch to preview
      }
    } catch (e) {
      console.warn('KA LLM generation failed', e);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 text-[11px]">
      {/* Toggle Bar */}
      <div className="flex items-center gap-2">
        <div className="flex rounded-md overflow-hidden border border-border/50">
          <button
            type="button"
            onClick={() => setShowPane('original')}
            className={`px-3 py-1 text-[10px] ${
              showPane === 'original'
                ? 'bg-[#18E78F]/15 text-[#18E78F] font-semibold'
                : 'bg-background/40 hover:bg-background/60'
            } transition-colors`}
          >
            Original
          </button>
          <button
            type="button"
            onClick={() => combinedPreview && setShowPane('preview')}
            disabled={!combinedPreview}
            className={`px-3 py-1 text-[10px] ${
              showPane === 'preview'
                ? 'bg-[#18E78F]/15 text-[#18E78F] font-semibold'
                : 'bg-background/40 hover:bg-background/60 disabled:opacity-40'
            } transition-colors`}
          >
            LLM Preview
          </button>
        </div>

        <button
          onClick={handleGenerateLLM}
          disabled={generating}
          className="ml-2 text-[10px] px-3 py-1 rounded-md border border-[#18E78F]/50 bg-[#18E78F]/10 hover:bg-[#18E78F]/20 disabled:opacity-40"
        >
          {generating ? 'Generating…' : (combinedPreview ? 'Regenerate' : 'Generate (LLM)')}
        </button>

        <div className="ml-auto flex gap-2">
          <button
            onClick={handleSave}
            disabled={disableSave || saving}
            className="text-[10px] px-3 py-1 rounded-md border border-[#18E78F]/50 bg-[#18E78F]/10 hover:bg-[#18E78F]/20 disabled:opacity-40"
          >
            {saving ? 'Saving…' : (savedId ? 'Saved' : 'Save Article')}
          </button>
          <button
            onClick={onClose}
            className="text-[10px] px-3 py-1 rounded-md border border-border/50 hover:bg-background/50"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Link Banner */}
      {savedId && (
        <div className="text-[10px] flex items-center gap-2 px-2 py-1 rounded-md border border-[#18E78F]/40 bg-[#18E78F]/10">
          <span className="font-semibold text-[#18E78F]">Article Saved</span>
          {docUrl ? (
            <a
              href={docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline text-[#18E78F]"
            >
              Open Document
            </a>
          ) : (
            <span className="text-muted-foreground">Document link not available.</span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto px-2 py-0.5 rounded border border-border/50 hover:bg-background/50"
          >
            Close
          </button>
        </div>
      )}

      {/* Preview Pane */}
      {showPane === 'preview' && (
        <div className="border border-border/50 rounded-md p-2 bg-background/50 text-[10px] whitespace-pre-wrap">
          {combinedPreview || 'No preview available.'}
        </div>
      )}

      {/* Original Editable Pane */}
      {showPane === 'original' && (
        <div className="grid gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Title
            </label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Knowledge Article Title"
              className="px-2 py-1.5 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Problem Description
            </label>
            <textarea
              rows={3}
              value={problem}
              onChange={e => setProblem(e.target.value)}
              placeholder="Describe the problem"
              className="resize-none px-2 py-1.5 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Business Impact
            </label>
            <textarea
              rows={3}
              value={impact}
              onChange={e => setImpact(e.target.value)}
              placeholder="Summarize business impact"
              className="resize-none px-2 py-1.5 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Resolution & Root Cause (Combined)
            </label>
            <textarea
              rows={5}
              value={resolutionRootCause}
              onChange={e => setResolutionRootCause(e.target.value)}
              placeholder="Add resolution details and root cause"
              className="resize-none px-2 py-1.5 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Follow-up / Next Steps
            </label>
            <textarea
              rows={3}
              value={nextSteps}
              onChange={e => setNextSteps(e.target.value)}
              placeholder="Planned remediation or monitoring actions"
              className="resize-none px-2 py-1.5 rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              All Updates (Read-only)
            </label>
            <textarea
              rows={6}
              value={allUpdatesText}
              readOnly
              placeholder="No updates captured"
              className="resize-none px-2 py-1.5 rounded-md bg-background/40 dark:bg-background/30 border border-border/50 text-muted-foreground/80"
            />
          </div>
        </div>
      )}
    </div>
  );
}